import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Bot, Plus, ScrollText, Trash2, Upload } from 'lucide-react';
import {
  hasPermission,
  Permission,
  PERMISSION_INFO,
  type AuditLogEntry,
  type Ban,
  type Invite,
  type NotificationLevel,
  type PermissionName,
  type Report,
  type Role,
  type Space,
  MAX_PERMISSIONS,
  permMinus,
  permOr,
} from '@spawnwire/shared';
import { useImageCropper } from '../../components/ImageCropper';
import { Avatar, Button, clsx, DropdownMenu, EmptyState, Field, Input, Select, Switch, TextArea, UserBadge } from '../../components/ui';
import { errorMessage, http, uploadFile } from '../../lib/api';
import { attempt, loadMembers } from '../../lib/data';
import { channelPath, navigate } from '../../lib/nav';
import { can, myTopRolePosition, useSpacePermissions } from '../../lib/perms';
import { formatTimestamp } from '../../lib/time';
import { actions, displayNameIn, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SpaceBots } from '../developer/SpaceBots';
import { DiscoverySettings } from '../discover/DiscoverySettings';
import { userMenu } from '../members/userMenu';
import { SpaceIcon } from '../shell/SpaceIcon';
import { Section, SettingsLayout } from './SettingsLayout';
import { SpaceWebhooks } from './SpaceWebhooks';
import { SpaceTagSettings } from './SpaceTagSettings';

function Overview({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]!);
  const channels = useStore((s) => s.channels);
  const perms = useSpacePermissions(spaceId);
  const editable = can(perms, Permission.MANAGE_SPACE);
  const [name, setName] = useState(space.name);
  const [description, setDescription] = useState(space.description);
  const [systemChannelId, setSystemChannelId] = useState(space.systemChannelId ?? '');
  const [uploading, setUploading] = useState<number | null>(null);
  const [bannerUploading, setBannerUploading] = useState<number | null>(null);
  const iconCropper = useImageCropper('icon', (f) => {
    setUploading(0);
    uploadFile(`/spaces/${spaceId}/icon`, f, setUploading)
      .promise.catch((err) => ui.toast(errorMessage(err), 'error'))
      .finally(() => setUploading(null));
  });
  const bannerCropper = useImageCropper('banner', (f) => {
    setBannerUploading(0);
    uploadFile(`/spaces/${spaceId}/banner`, f, setBannerUploading)
      .promise.catch((err) => ui.toast(errorMessage(err), 'error'))
      .finally(() => setBannerUploading(null));
  });
  const textChannels = Object.values(channels).filter((c) => c.spaceId === spaceId && c.type === 'text');
  const dirty = name !== space.name || description !== space.description || systemChannelId !== (space.systemChannelId ?? '');

  return (
    <>
      <Section>
        <div className="flex items-center gap-5">
          <SpaceIcon name={space.name} iconUrl={space.iconUrl} size={88} />
          {editable && (
            <div className="space-y-2">
              <label className={clsx('inline-flex cursor-pointer items-center gap-2 rounded-md bg-active px-4 py-2 text-sm font-medium text-fg hover:bg-line-strong', uploading !== null && 'opacity-60')}>
                <Upload className="size-4" aria-hidden />
                {uploading !== null ? `Uploading ${Math.round(uploading * 100)}%` : 'Upload icon'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) iconCropper.pick(f);
                  }}
                />
              </label>
              {space.iconUrl && (
                <Button variant="ghost" size="sm" onClick={() => void attempt(() => http.del(`/spaces/${spaceId}/icon`))}>
                  Remove icon
                </Button>
              )}
              <p className="text-xs text-faint">PNG, JPEG, GIF, WebP or AVIF. You can crop it after picking; animated images upload as they are.</p>
            </div>
          )}
        </div>
      </Section>
      <Section title="Banner" description="Shown at the top of your channel list and on your Discover card.">
        <div className="relative aspect-[3/1] max-w-lg overflow-hidden rounded-xl border border-line bg-sidebar">
          {space.bannerUrl ? (
            <img src={space.bannerUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="flex size-full items-center justify-center text-sm text-faint">No banner yet</span>
          )}
        </div>
        {editable && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className={clsx('inline-flex cursor-pointer items-center gap-2 rounded-md bg-active px-4 py-2 text-sm font-medium text-fg hover:bg-line-strong', bannerUploading !== null && 'opacity-60')}>
              <Upload className="size-4" aria-hidden />
              {bannerUploading !== null ? `Uploading ${Math.round(bannerUploading * 100)}%` : 'Upload banner'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) bannerCropper.pick(f);
                }}
              />
            </label>
            {space.bannerUrl && (
              <Button variant="ghost" size="sm" onClick={() => void attempt(() => http.del(`/spaces/${spaceId}/banner`))}>
                Remove banner
              </Button>
            )}
            <p className="w-full text-xs text-faint">Cropped to 3:1 after picking. Animated images upload as they are.</p>
          </div>
        )}
        {iconCropper.dialog}
        {bannerCropper.dialog}
      </Section>
      <Section>
        <Field label="Space name" htmlFor="ov-name">
          <Input id="ov-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!editable} maxLength={100} />
        </Field>
        <Field label="Description" htmlFor="ov-desc">
          <TextArea id="ov-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!editable} maxLength={500} />
        </Field>
        <Field label="Welcome messages channel" htmlFor="ov-sys" hint="Where Spawnwire posts a message when someone joins.">
          <Select id="ov-sys" value={systemChannelId} onChange={setSystemChannelId} options={[{ value: '', label: 'No welcome messages' }, ...textChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))]} />
        </Field>
        {editable && (
          <Button disabled={!dirty || name.trim().length < 2} onClick={() => void attempt(() => http.patch(`/spaces/${spaceId}`, { name, description, systemChannelId: systemChannelId || null }), 'Saved.')}>
            Save changes
          </Button>
        )}
      </Section>
    </>
  );
}

function NotificationsSection({ spaceId }: { spaceId: string }) {
  const setting = useStore((s) => s.notificationSettings[spaceId]);
  const level = setting?.level ?? 'mentions';
  const muted = !!setting?.mutedUntil && new Date(setting.mutedUntil).getTime() > Date.now();
  const save = (patch: object) => void attempt(() => http.put(`/users/@me/notification-settings/${spaceId}`, patch));
  return (
    <Section description="These apply only to you.">
      <Field label="Notify me about" htmlFor="ns-level">
        <Select<NotificationLevel> id="ns-level" value={level} onChange={(v) => save({ level: v })} options={[{ value: 'all', label: 'All messages' }, { value: 'mentions', label: 'Only mentions' }, { value: 'nothing', label: 'Nothing' }]} />
      </Field>
      <Switch checked={!!setting?.suppressEveryone} onChange={(v) => save({ suppressEveryone: v })} label="Suppress @everyone and @here" />
      <Switch checked={muted} onChange={(v) => save({ mutedUntil: v ? new Date(Date.now() + 365 * 86400_000).toISOString() : null })} label="Mute this space" description="Unread markers still show, but there are no notifications or sounds." />
    </Section>
  );
}

const PERMISSION_GROUPS = ['General', 'Membership', 'Text', 'Voice', 'Bots', 'Advanced'] as const;

export function PermissionToggles({ value, onChange, disabled, allowed }: { value: number; onChange: (v: number) => void; disabled?: boolean; allowed: number }) {
  return (
    <div className="space-y-6">
      {PERMISSION_GROUPS.map((group) => (
        <div key={group}>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">{group}</h3>
          <div className="divide-y divide-line">
            {(Object.keys(PERMISSION_INFO) as PermissionName[])
              .filter((p) => PERMISSION_INFO[p].group === group)
              .map((p) => {
                const bit = Permission[p];
                return (
                  <Switch
                    key={p}
                    checked={hasPermission(value, bit)}
                    disabled={disabled || !hasPermission(allowed, bit)}
                    onChange={(on) => onChange(on ? permOr(value, bit) : permMinus(value, bit))}
                    label={PERMISSION_INFO[p].label}
                    description={PERMISSION_INFO[p].description}
                  />
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}

function RoleEditor({ role, editable, allowed }: { role: Role; editable: boolean; allowed: number }) {
  const botName = useStore((s) => (role.botId ? s.users[role.botId]?.displayName : undefined));
  const [name, setName] = useState(role.name);
  const [color, setColor] = useState(role.color ?? '#9aa1ad');
  const [hasColor, setHasColor] = useState(!!role.color);
  const [permissions, setPermissions] = useState(role.permissions);
  const [hoist, setHoist] = useState(role.hoist);
  const [mentionable, setMentionable] = useState(role.mentionable);
  useEffect(() => {
    setName(role.name);
    setColor(role.color ?? '#9aa1ad');
    setHasColor(!!role.color);
    setPermissions(role.permissions);
    setHoist(role.hoist);
    setMentionable(role.mentionable);
  }, [role]);
  const dirty = name !== role.name || (hasColor ? color : null) !== role.color || permissions !== role.permissions || hoist !== role.hoist || mentionable !== role.mentionable;
  const save = () =>
    void attempt(
      () =>
        http.patch(
          `/spaces/${role.spaceId}/roles/${role.id}`,
          role.isDefault ? { permissions } : { name, color: hasColor ? color : null, permissions, hoist, mentionable },
        ),
      'Role saved.',
    );

  return (
    <div className="space-y-5">
      {!role.isDefault && (
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Role name" htmlFor="role-name">
            <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!editable} maxLength={100} />
          </Field>
          <Field label="Colour">
            <div className="flex h-10 items-center gap-2">
              <input type="color" value={color} onChange={(e) => { setColor(e.target.value); setHasColor(true); }} disabled={!editable} className="h-9 w-14 cursor-pointer rounded border border-line bg-transparent" aria-label="Role colour" />
              {hasColor && (
                <Button size="sm" variant="ghost" disabled={!editable} onClick={() => setHasColor(false)}>
                  No colour
                </Button>
              )}
            </div>
          </Field>
        </div>
      )}
      {!role.isDefault && (
        <div>
          <Switch checked={hoist} onChange={setHoist} disabled={!editable} label="Show members separately" description="List members with this role in their own group in the member list." />
          <Switch checked={mentionable} onChange={setMentionable} disabled={!editable} label="Allow anyone to @mention this role" />
        </div>
      )}
      {role.isDefault && <p className="text-sm text-muted">@everyone is every member of the space. Its permissions are the baseline every role builds on. Bots don't get them: a bot has only what its own role and any roles you give it allow.</p>}
      {role.botId && (
        <p className="flex items-start gap-2 rounded-md bg-accent-soft px-3 py-2 text-sm text-fg">
          <Bot className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
          <span>
            This role was made for the bot <span className="font-semibold">{botName ?? 'that was added'}</span> and holds what it was given when it was added. It can't be given to anyone else, and it is removed with the bot. Move it up the list to let the bot manage or moderate members with lower roles.
          </span>
        </p>
      )}
      <PermissionToggles value={permissions} onChange={setPermissions} disabled={!editable} allowed={allowed} />
      {editable && (
        <div className="sticky bottom-0 flex gap-2 border-t border-line bg-main py-3">
          <Button disabled={!dirty} onClick={save}>
            Save role
          </Button>
          {!role.isDefault && !role.botId && (
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() =>
                ui.confirm({
                  title: `Delete the ${role.name} role?`,
                  body: 'Members with this role lose it. This cannot be undone.',
                  confirmLabel: 'Delete role',
                  danger: true,
                  onConfirm: () => http.del(`/spaces/${role.spaceId}/roles/${role.id}`),
                })
              }
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Roles({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]!);
  const perms = useSpacePermissions(spaceId);
  const top = useStore((s) => myTopRolePosition(s, spaceId));
  const isAdmin = space.ownerId === useStore.getState().me?.id || hasPermission(perms, Permission.ADMINISTRATOR);
  const allowed = isAdmin ? MAX_PERMISSIONS : perms;
  const manage = can(perms, Permission.MANAGE_ROLES);
  const roles = useMemo(() => [...space.roles].sort((a, b) => b.position - a.position), [space.roles]);
  const [selected, setSelected] = useState<string | null>(roles.find((r) => !r.isDefault)?.id ?? roles[0]?.id ?? null);
  const role = roles.find((r) => r.id === selected);

  async function move(r: Role, dir: 1 | -1) {
    const movable = roles.filter((x) => !x.isDefault);
    const idx = movable.findIndex((x) => x.id === r.id);
    const swap = movable[idx - dir];
    if (!swap) return;
    await attempt(() => http.put(`/spaces/${spaceId}/roles/order`, [{ id: r.id, position: swap.position }, { id: swap.id, position: r.position }]));
  }

  return (
    <div className="grid gap-6 md:grid-cols-[220px_1fr]">
      <div>
        {manage && (
          <Button
            className="mb-3 w-full"
            icon={Plus}
            onClick={() =>
              void attempt(() => http.post<Role>(`/spaces/${spaceId}/roles`, { name: 'New role' })).then((r) => {
                if (r) {
                  actions.upsertRole(r);
                  setSelected(r.id);
                }
              })
            }
          >
            Create role
          </Button>
        )}
        <ul className="space-y-0.5">
          {roles.map((r) => (
            <li key={r.id} className="group flex items-center">
              <button type="button" onClick={() => setSelected(r.id)} className={clsx('flex min-w-0 flex-1 items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm', selected === r.id ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}>
                <span className="size-3 shrink-0 rounded-full" style={{ background: r.color ?? 'var(--p-faint)' }} aria-hidden />
                <span className="truncate">{r.name}</span>
                {r.botId && <Bot className="size-3.5 shrink-0 text-faint" aria-label="Bot role" />}
              </button>
              {manage && !r.isDefault && r.position < top && (
                <span className="hidden gap-0.5 group-hover:flex">
                  <button type="button" aria-label={`Move ${r.name} up`} className="rounded p-1 text-faint hover:text-fg disabled:opacity-30" disabled={r.position + 1 >= top} onClick={() => void move(r, 1)}>
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button type="button" aria-label={`Move ${r.name} down`} className="rounded p-1 text-faint hover:text-fg disabled:opacity-30" disabled={r.position <= 1} onClick={() => void move(r, -1)}>
                    <ArrowDown className="size-3.5" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-faint">Higher roles outrank lower ones. You can only edit roles below your highest role.</p>
      </div>
      <div>{role ? <RoleEditor role={role} editable={manage && (role.isDefault || role.position < top)} allowed={allowed} /> : <p className="text-sm text-muted">Select a role.</p>}</div>
    </div>
  );
}

function Members({ spaceId }: { spaceId: string }) {
  const members = useStore((s) => s.members[spaceId]);
  const space = useStore((s) => s.spaces[spaceId]!);
  const [q, setQ] = useState('');
  useEffect(() => {
    void loadMembers(spaceId, true).catch(() => {});
  }, [spaceId]);
  const list = Object.values(members ?? {})
    .filter((m) => !q || m.user.username.includes(q.toLowerCase()) || m.user.displayName.toLowerCase().includes(q.toLowerCase()) || (m.nickname ?? '').toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));
  return (
    <>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" aria-label="Search members" className="mb-4" />
      <p className="mb-2 text-xs text-faint">{space.memberCount} members</p>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {list.map((m) => (
          <li key={m.user.id} className="flex items-center gap-3 px-3 py-2">
            <Avatar user={m.user} size={32} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-fg">
                {displayNameIn(useStore.getState(), m.user.id, spaceId)}
                <UserBadge user={m.user} /> <span className="text-xs text-faint">@{m.user.username}</span>
              </div>
              <div className="flex flex-wrap gap-1 pt-0.5">
                {space.roles
                  .filter((r) => m.roleIds.includes(r.id))
                  .map((r) => (
                    <span key={r.id} className="rounded bg-active px-1.5 text-[11px] text-muted" style={r.color ? { color: r.color } : undefined}>
                      {r.name}
                    </span>
                  ))}
                {m.timeoutUntil && <span className="rounded bg-danger-soft px-1.5 text-[11px] text-danger">Timed out</span>}
              </div>
            </div>
            <span className="hidden text-xs text-faint sm:block">Joined {new Date(m.joinedAt).toLocaleDateString()}</span>
            <DropdownMenu entries={userMenu(m.user.id, spaceId)} trigger={<Button size="sm" variant="secondary">Manage</Button>} />
          </li>
        ))}
      </ul>
    </>
  );
}

function Invites({ spaceId }: { spaceId: string }) {
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => void http.get<Invite[]>(`/spaces/${spaceId}/invites`).then(setInvites).catch((err) => setError(errorMessage(err))), [spaceId]);
  useEffect(load, [load]);
  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!invites) return <p className="text-sm text-muted">Loading invites</p>;
  const now = Date.now();
  return invites.length === 0 ? (
    <EmptyState icon={ScrollText} title="No invites yet" body="Create one from the space menu." />
  ) : (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full text-sm">
        <thead className="bg-sidebar text-left text-xs uppercase tracking-wide text-faint">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Created by</th>
            <th className="px-3 py-2">Uses</th>
            <th className="px-3 py-2">Expires</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {invites.map((i) => {
            const expired = i.revoked || (i.expiresAt && new Date(i.expiresAt).getTime() < now) || (i.maxUses > 0 && i.uses >= i.maxUses);
            return (
              <tr key={i.code} className={clsx(expired && 'opacity-50')}>
                <td className="px-3 py-2 font-mono text-fg">{i.code}</td>
                <td className="px-3 py-2 text-muted">{i.inviter?.displayName ?? 'Unknown'}</td>
                <td className="px-3 py-2 text-muted">
                  {i.uses}
                  {i.maxUses ? ` / ${i.maxUses}` : ''}
                </td>
                <td className="px-3 py-2 text-muted">{i.revoked ? 'Revoked' : i.expiresAt ? formatTimestamp(i.expiresAt) : 'Never'}</td>
                <td className="px-3 py-2 text-right">
                  {!expired && (
                    <Button size="sm" variant="ghost" onClick={() => void attempt(() => http.del(`/invites/${i.code}`)).then(load)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Bans({ spaceId }: { spaceId: string }) {
  const [bans, setBans] = useState<Ban[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => void http.get<Ban[]>(`/spaces/${spaceId}/bans`).then(setBans).catch((err) => setError(errorMessage(err))), [spaceId]);
  useEffect(load, [load]);
  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!bans) return <p className="text-sm text-muted">Loading bans</p>;
  return bans.length === 0 ? (
    <EmptyState icon={ScrollText} title="No bans" body="Banned people show up here and can be unbanned." />
  ) : (
    <ul className="divide-y divide-line rounded-lg border border-line">
      {bans.map((b) => (
        <li key={b.user.id} className="flex items-center gap-3 px-3 py-2">
          <Avatar user={b.user} size={32} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-fg">
              {b.user.displayName}
              <UserBadge user={b.user} />
            </div>
            <div className="truncate text-xs text-faint">{b.reason || 'No reason given'} {'·'} {formatTimestamp(b.createdAt)}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void attempt(() => http.del(`/spaces/${spaceId}/bans/${b.user.id}`), 'Unbanned.').then(load)}>
            Unban
          </Button>
        </li>
      ))}
    </ul>
  );
}

const ACTION_LABELS: Record<AuditLogEntry['action'], string> = {
  space_update: 'updated the space',
  ownership_transfer: 'transferred ownership',
  channel_create: 'created a channel',
  channel_update: 'updated a channel',
  channel_delete: 'deleted a channel',
  overwrite_update: 'changed channel permissions',
  overwrite_delete: 'removed channel permissions',
  role_create: 'created a role',
  role_update: 'updated a role',
  role_delete: 'deleted a role',
  member_update: 'updated a member',
  member_roles_update: 'changed member roles',
  member_kick: 'kicked a member',
  member_ban: 'banned a member',
  member_unban: 'unbanned a member',
  member_timeout: 'timed out a member',
  invite_create: 'created an invite',
  invite_revoke: 'revoked an invite',
  message_delete: 'deleted a message',
  message_pin: 'pinned a message',
  message_unpin: 'unpinned a message',
  message_publish: 'published an announcement',
  voice_moderate: 'moderated voice',
  report_resolve: 'updated a report',
  bot_add: 'added a bot',
  bot_remove: 'removed a bot',
  event_create: 'scheduled an event',
  event_update: 'updated an event',
  event_delete: 'deleted an event',
  webhook_create: 'created a webhook',
  webhook_update: 'changed a webhook',
  webhook_delete: 'deleted a webhook',
};

function describeTarget(e: AuditLogEntry, spaceId: string): string {
  const state = useStore.getState();
  if (!e.targetId) return '';
  if (e.targetType === 'user') return displayNameIn(state, e.targetId, spaceId);
  if (e.targetType === 'channel') return state.channels[e.targetId] ? `#${state.channels[e.targetId]!.name}` : String((e.changes.name as unknown[] | undefined)?.[0] ?? 'a deleted channel');
  if (e.targetType === 'role') return state.spaces[spaceId]?.roles.find((r) => r.id === e.targetId)?.name ?? String((e.changes.name as unknown[] | undefined)?.[0] ?? 'a deleted role');
  // Events keep their name in the entry, since the event itself may be gone by the time someone reads this.
  if (e.targetType === 'event' || e.targetType === 'webhook') return typeof e.changes.name === 'string' ? e.changes.name : e.targetType === 'webhook' ? 'a webhook' : 'an event';
  return '';
}

function AuditLog({ spaceId }: { spaceId: string }) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [done, setDone] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fetchPage = useCallback(
    async (before?: string) => {
      const params = new URLSearchParams({ limit: '50' });
      if (before) params.set('before', before);
      if (action) params.set('action', action);
      return http.get<AuditLogEntry[]>(`/spaces/${spaceId}/audit-logs?${params}`);
    },
    [spaceId, action],
  );
  async function load(reset: boolean) {
    try {
      const list = await fetchPage(reset ? undefined : entries.at(-1)?.id);
      setEntries(reset ? list : [...entries, ...list]);
      setDone(list.length < 50);
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  useEffect(() => {
    fetchPage()
      .then((list) => {
        setEntries(list);
        setDone(list.length < 50);
      })
      .catch((err) => setError(errorMessage(err)));
  }, [fetchPage]);
  if (error) return <p className="text-sm text-danger">{error}</p>;
  return (
    <>
      <Select value={action} onChange={setAction} options={[{ value: '', label: 'All actions' }, ...Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label: label[0]!.toUpperCase() + label.slice(1) }))]} className="mb-4 max-w-xs" />
      {entries.length === 0 ? (
        <EmptyState icon={ScrollText} title="Nothing logged yet" body="Administrative actions are recorded here." />
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {entries.map((e) => (
            <li key={e.id} className="flex items-start gap-3 px-3 py-2.5">
              <Avatar user={e.actor} size={28} />
              <div className="min-w-0 flex-1 text-sm">
                <span className="font-medium text-fg">{e.actor?.displayName ?? 'Someone'}</span>
                <UserBadge user={e.actor} /> <span className="text-muted">{ACTION_LABELS[e.action]}</span>{' '}
                <span className="font-medium text-fg">{describeTarget(e, spaceId)}</span>
                {e.reason && <div className="text-xs text-muted">Reason: {e.reason}</div>}
                {Object.keys(e.changes).length > 0 && <div className="truncate font-mono text-[11px] text-faint">{JSON.stringify(e.changes)}</div>}
              </div>
              <time className="shrink-0 text-xs text-faint" dateTime={e.createdAt}>
                {formatTimestamp(e.createdAt)}
              </time>
            </li>
          ))}
        </ul>
      )}
      {!done && entries.length > 0 && (
        <Button className="mt-3" variant="secondary" onClick={() => void load(false)}>
          Load more
        </Button>
      )}
    </>
  );
}

function Reports({ spaceId }: { spaceId: string }) {
  const [status, setStatus] = useState<'open' | 'resolved' | 'dismissed'>('open');
  const [reports, setReports] = useState<Report[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => void http.get<Report[]>(`/spaces/${spaceId}/reports?status=${status}`).then(setReports).catch((err) => setError(errorMessage(err))), [spaceId, status]);
  useEffect(load, [load]);
  const resolve = (r: Report, next: 'resolved' | 'dismissed' | 'open') => void attempt(() => http.patch(`/reports/${r.id}`, { status: next })).then(load);
  if (error) return <p className="text-sm text-danger">{error}</p>;
  return (
    <>
      <Select value={status} onChange={setStatus} options={[{ value: 'open', label: 'Open' }, { value: 'resolved', label: 'Resolved' }, { value: 'dismissed', label: 'Dismissed' }]} className="mb-4 max-w-xs" />
      {!reports ? (
        <p className="text-sm text-muted">Loading reports</p>
      ) : reports.length === 0 ? (
        <EmptyState icon={ScrollText} title={`No ${status} reports`} />
      ) : (
        <ul className="space-y-3">
          {reports.map((r) => (
            <li key={r.id} className="rounded-lg border border-line p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded bg-danger-soft px-2 py-0.5 text-xs font-medium text-danger">{r.category.replace('_', ' ')}</span>
                <span className="text-muted">
                  {r.reporter?.displayName ?? 'Someone'} reported {r.targetUser?.displayName ?? 'a user'} {'·'} {formatTimestamp(r.createdAt)}
                </span>
              </div>
              {r.reason && <p className="mt-2 text-sm text-fg">{r.reason}</p>}
              {r.messageContent !== null && <blockquote className="mt-2 border-l-4 border-line-strong pl-3 text-sm text-muted">{r.messageContent || '(no text)'}</blockquote>}
              {r.resolvedBy && <p className="mt-2 text-xs text-faint">Handled by {r.resolvedBy.displayName}{r.resolutionNote ? `: ${r.resolutionNote}` : ''}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {r.messageId && r.channelId && useStore.getState().channels[r.channelId] && (
                  <Button size="sm" variant="secondary" onClick={() => { ui.close(); navigate(`${channelPath(useStore.getState().channels[r.channelId!]!)}?m=${r.messageId}`); }}>
                    View message
                  </Button>
                )}
                {r.status === 'open' ? (
                  <>
                    <Button size="sm" onClick={() => resolve(r, 'resolved')}>
                      Mark resolved
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => resolve(r, 'dismissed')}>
                      Dismiss
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => resolve(r, 'open')}>
                    Reopen
                  </Button>
                )}
                {r.targetUser && (
                  <DropdownMenu entries={userMenu(r.targetUser.id, spaceId).filter((e) => ['Time out', 'Kick', 'Ban'].includes(e.label))} trigger={<Button size="sm" variant="secondary">Act on user</Button>} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Ownership({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]!);
  const members = useStore((s) => s.members[spaceId]);
  const meId = useStore((s) => s.me?.id);
  const [target, setTarget] = useState('');
  const [password, setPassword] = useState('');
  const [confirmName, setConfirmName] = useState('');
  useEffect(() => {
    void loadMembers(spaceId).catch(() => {});
  }, [spaceId]);
  const candidates = Object.values(members ?? {}).filter((m) => m.user.id !== meId && !m.user.bot);
  return (
    <>
      <Section title="Transfer ownership" description="The new owner gets full control. You keep your roles but lose owner powers.">
        <Select value={target} onChange={setTarget} options={[{ value: '', label: 'Choose a member' }, ...candidates.map((m) => ({ value: m.user.id, label: `${m.user.displayName} (@${m.user.username})` }))]} />
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" aria-label="Your password" autoComplete="current-password" />
        <Button
          variant="danger"
          disabled={!target || !password}
          onClick={() =>
            ui.confirm({
              title: 'Transfer ownership?',
              body: 'Only the new owner can give it back.',
              confirmLabel: 'Transfer',
              danger: true,
              onConfirm: async () => {
                await http.post(`/spaces/${spaceId}/transfer`, { userId: target, password });
                setPassword('');
                ui.toast('Ownership transferred.', 'success');
              },
            })
          }
        >
          Transfer ownership
        </Button>
      </Section>
      <Section title="Delete space" description={`Deletes ${space.name}, its channels, messages and files for everyone. This cannot be undone.`}>
        <Field label={`Type "${space.name}" to confirm`} htmlFor="del-name">
          <Input id="del-name" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
        </Field>
        <Button
          variant="danger"
          icon={Trash2}
          disabled={confirmName !== space.name}
          onClick={() =>
            void attempt(async () => {
              await http.del(`/spaces/${spaceId}`, { name: confirmName });
              ui.close();
              navigate('/app/@me');
            })
          }
        >
          Delete space
        </Button>
      </Section>
    </>
  );
}

export function SpaceSettings({ spaceId, section }: { spaceId: string; section?: string }) {
  const space = useStore((s) => s.spaces[spaceId]) as Space | undefined;
  const perms = useSpacePermissions(spaceId);
  const meId = useStore((s) => s.me?.id);
  if (!space) return null;
  const owner = space.ownerId === meId;
  return (
    <SettingsLayout
      title={space.name}
      initial={section}
      sections={[
        { id: 'overview', label: 'Overview', group: space.name, render: () => <Overview spaceId={spaceId} /> },
        { id: 'notifications', label: 'Notifications', group: space.name, render: () => <NotificationsSection spaceId={spaceId} /> },
        ...(can(perms, Permission.MANAGE_ROLES) ? [{ id: 'roles', label: 'Roles', group: space.name, render: () => <Roles spaceId={spaceId} /> }] : []),
        ...(owner ? [{ id: 'discovery', label: 'Discovery', group: space.name, render: () => <DiscoverySettings spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.MANAGE_SPACE) ? [{ id: 'tag', label: 'Space tag', group: space.name, render: () => <SpaceTagSettings spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.KICK_MEMBERS) || can(perms, Permission.BAN_MEMBERS) || can(perms, Permission.MODERATE_MEMBERS) || can(perms, Permission.MANAGE_ROLES)
          ? [{ id: 'members', label: 'Members', group: 'People', render: () => <Members spaceId={spaceId} /> }]
          : []),
        ...(can(perms, Permission.MANAGE_SPACE) ? [{ id: 'invites', label: 'Invites', group: 'People', render: () => <Invites spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.MANAGE_SPACE) ? [{ id: 'bots', label: 'Bots', group: 'People', render: () => <SpaceBots spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.MANAGE_WEBHOOKS) ? [{ id: 'webhooks', label: 'Webhooks', group: 'People', render: () => <SpaceWebhooks spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.BAN_MEMBERS) ? [{ id: 'bans', label: 'Bans', group: 'Moderation', render: () => <Bans spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.MODERATE_MEMBERS) ? [{ id: 'reports', label: 'Reports', group: 'Moderation', render: () => <Reports spaceId={spaceId} /> }] : []),
        ...(can(perms, Permission.VIEW_AUDIT_LOG) ? [{ id: 'audit', label: 'Audit log', group: 'Moderation', render: () => <AuditLog spaceId={spaceId} /> }] : []),
        ...(owner ? [{ id: 'ownership', label: 'Ownership and deletion', group: 'Danger zone', render: () => <Ownership spaceId={spaceId} /> }] : []),
      ]}
    />
  );
}
